import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Reorder } from 'framer-motion';
// Hooks
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Types
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
// Models
import IQuiz from 'models/Quiz';
import IQuestion from 'models/Question';
import IAnswer, { IAnswerResult } from 'models/Answer';
// Actions
import { setAnswers, setQuestionEnd, setQuestionStart } from 'store/player/playerSlice';
// Selectors
import { selectQuiz, selectQuestion } from 'store/quiz/quizSelectors';
// Components
import { Button } from 'components/Buttons';
// Icons
import { MdDragIndicator } from 'react-icons/md';
// Utilities
import { shuffle } from 'utilities/Utilities';

type Props = {
  time: number;
  disabled: boolean;
}

const RankingAnswers:FC<Props> = ({ time, disabled }) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz = useAppSelector(selectQuiz);
  const question:IQuestion = useAppSelector(selectQuestion);

  const [items, setItems] = useState(shuffle(question.answers));

  const reorder = (items:any) => setItems(items);

  const [ clicked, setClicked ] = useState<boolean>(false);

  const handleSubmit = () => {
    if (clicked) return;
  
    dispatch(setQuestionStart(false));

    const correctAnswers = items.filter((item:any, idx:number) => item.ref === question.answers[idx].ref);

    const score = question.score / question.answers.length * correctAnswers.length;

    const nextAnswer:IAnswerResult = {
      type: question.type,
      input: {
        choices: items.map((item:any) => ({
          ref: item.ref,
          title: item.title
        }))
      },
      score,
      time,
      correct: correctAnswers.length > 0,
      question: {
        ref: question.ref,
        title: question.title
      }
    }

    dispatch(setAnswers([nextAnswer]));
    dispatch(setQuestionEnd(true));

    setClicked(true);
  }

  const { correctAnswerColor, incorrectAnswerColor } = quiz.questionsSettings

  if ( quiz.type !== QuizTypes.Scoring || question.type !== QuestionTypes.Ranking ) return null;
  return (
    <div className="py-2 px-4">
      <div className="flex flex-col gap-4 mb-4">
        <Reorder.Group axis="y" values={items} as="div" onReorder={reorder} className="flex flex-col gap-2 mb-4 overscroll-none">
          {items.map((answer:any, idx:number) => {
            const isCorrectPosition = idx === question.answers.findIndex((a:IAnswer) => a.ref === answer.ref);
            const backgroundColor = clicked ? (isCorrectPosition ? correctAnswerColor : incorrectAnswerColor) : undefined;
            const title = !clicked ? answer.title : answer.postAnswerTitle || answer.title;
            return (
              <Reorder.Item
                key={`answer-item-${answer.ref}`} as="div"
                value={answer}
                style={{ touchAction: 'none' }}
              >
                <Button
                  className="w-full relative normal-case font-bold"
                  style={{
                    backgroundColor,
                    color: backgroundColor ? 'white' : undefined,
                    touchAction: 'none'
                  }}
                  onPointerDownCapture={(e) => {
                    e.preventDefault();
                  }}
                >
                  <div className="w-full flex items-center justify-between gap-4">
                    <MdDragIndicator />
                    <span className={disabled ? 'blur-sm  select-none' : ''}>{title}</span>
                    <span />
                  </div>
                </Button>
              </Reorder.Item>
            )
          })}
        </Reorder.Group>
        <Button
          className="w-full text-xl uppercase primary-button"
          onClick={handleSubmit}
          disabled={disabled || clicked}
        >
          {t('buttons.submit')}
        </Button>
      </div>
    </div>
  )
}

export default RankingAnswers;
